import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Chess, Move, Square } from 'chess.js';
import { Chessboard } from 'react-chessboard';
import { useAuth, useAppSocketContext } from '@/shared/hooks';
import ChessGameInfo from './GameInfo';
import type { User } from '@/shared/types';

type AppliedMove = {
  notation: string;
  from: string;
  to: string;
  promotion?: string;
};

function notationToMove(notation: string): { from: string; to: string; promotion?: string } {
  const from = notation.slice(0, 2);
  const to = notation.slice(2, 4);
  const promotion = notation.length > 4 ? notation[4] : undefined;
  return { from, to, promotion };
}

function moveToNotation(move: { from: string; to: string; promotion?: string }): string {
  return `${move.from}${move.to}${move.promotion ?? ''}`;
}

function buildGameFromMoves(moves: AppliedMove[]): Chess {
  const g = new Chess();
  for (const m of moves) {
    const applied = g.move({ from: m.from, to: m.to, promotion: m.promotion as any });
    if (!applied) {
      break;
    }
  }
  return g;
}

function ChessGame({
  user,
  gameId,
  whiteId,
  blackId,
  tournamentId,
  whiteUsername,
  blackUsername,
  startTime,
  isMuted,
}: {
  user: User | null;
  gameId: number;
  whiteId: number;
  blackId: number;
  tournamentId: number;
  whiteUsername: string;
  blackUsername: string;
  startTime: number;
  isMuted: boolean;
}) {
  const { incomingMove, setIncomingMove } = useAppSocketContext();
  const { firebaseUser, loading } = useAuth();

  const playerColor = user?.id === whiteId ? 'w' : user?.id === blackId ? 'b' : null;

  const [serverMoves, setServerMoves] = useState<AppliedMove[]>([]);
  const [pendingMove, setPendingMove] = useState<AppliedMove | null>(null);
  const [isHydrated, setIsHydrated] = useState(false);
  const [isSubmittingMove, setIsSubmittingMove] = useState(false);
  const [activeSquare, setActiveSquare] = useState<string | null>(null);
  const [legalMoves, setLegalMoves] = useState<string[]>([]);

  const hasFetchedInitialMovesRef = useRef(false);

  const viewMoves = useMemo(() => {
    if (!pendingMove) return serverMoves;
    return [...serverMoves, pendingMove];
  }, [serverMoves, pendingMove]);

  const game = useMemo(() => buildGameFromMoves(viewMoves), [viewMoves]);

  const resetIncomingMove = useCallback(() => setIncomingMove(null), [setIncomingMove]);

  const fetchMoves = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 7000);
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tournamentMoveHistory?gameId=${gameId}`, {
        signal: controller.signal,
      });
      clearTimeout(timeout);
      if (!res.ok) {
        throw new Error(`Failed to fetch move history: ${res.statusText}`);
      }
      const data = await res.json();
      const mapped: AppliedMove[] = Array.isArray(data)
        ? data.map((notation: string) => {
            const parsed = notationToMove(notation);
            return {
              notation,
              from: parsed.from,
              to: parsed.to,
              promotion: parsed.promotion,
            };
          })
        : [];

      setServerMoves(mapped);
      setPendingMove(null);
      setIsHydrated(true);
    } catch (error) {
      console.error('[ChessGame] Failed to fetch move history:', error);
      setIsHydrated(true);
    }
  }, [gameId]);

  useEffect(() => {
    setServerMoves([]);
    setPendingMove(null);
    setIsHydrated(false);
    setIsSubmittingMove(false);
    setActiveSquare(null);
    setLegalMoves([]);
    hasFetchedInitialMovesRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId]);

  useEffect(() => {
    if (!hasFetchedInitialMovesRef.current) {
      hasFetchedInitialMovesRef.current = true;
      fetchMoves();
    }
  }, [fetchMoves]);

  useEffect(() => {
    if (!incomingMove || !isHydrated) return;
    if (incomingMove.gameId !== gameId) {
      resetIncomingMove();
      return;
    }

    const notation = moveToNotation({ from: incomingMove.from, to: incomingMove.to, promotion: incomingMove.promotion });

    setServerMoves((prev) => {
      const last = prev[prev.length - 1];
      if (last?.notation === notation) {
        return prev;
      }
      return [
        ...prev,
        {
          notation,
          from: incomingMove.from,
          to: incomingMove.to,
          promotion: incomingMove.promotion,
        },
      ];
    });

    setPendingMove((currentPending) => {
      if (!currentPending) return currentPending;
      return currentPending.notation === notation ? null : currentPending;
    });

    resetIncomingMove();
  }, [incomingMove, isHydrated, gameId, resetIncomingMove]);

  const onPieceDragBegin = (piece: string, sourceSquare: string) => {
    if (!playerColor || !isHydrated || isSubmittingMove || game.turn() !== playerColor) {
      setActiveSquare(null);
      setLegalMoves([]);
      return;
    }

    const sourcePiece = game.get(sourceSquare as Square);
    if (!sourcePiece || sourcePiece.color !== playerColor) {
      setActiveSquare(null);
      setLegalMoves([]);
      return;
    }

    const moves = game.moves({ square: sourceSquare as Square, verbose: true });
    setActiveSquare(sourceSquare);
    setLegalMoves(moves.map((m) => m.to));
  };

  const onDrop = (sourceSquare: string, targetSquare: string): boolean => {
    if (!playerColor || !isHydrated || isSubmittingMove || loading || !firebaseUser) {
      return false;
    }

    if (game.turn() !== playerColor) {
      return false;
    }

    const piece = game.get(sourceSquare as Square);
    if (!piece || piece.color !== playerColor) {
      return false;
    }

    const isPromotion =
      piece.type === 'p' &&
      ((piece.color === 'w' && targetSquare[1] === '8') || (piece.color === 'b' && targetSquare[1] === '1'));

    const testGame = new Chess(game.fen());
    const applied = testGame.move({
      from: sourceSquare,
      to: targetSquare,
      ...(isPromotion ? { promotion: 'q' } : {}),
    });

    if (!applied) {
      return false;
    }

    const notation = moveToNotation({ from: applied.from, to: applied.to, promotion: applied.promotion });
    const optimistic: AppliedMove = {
      notation,
      from: applied.from,
      to: applied.to,
      promotion: applied.promotion,
    };

    setPendingMove(optimistic);
    setIsSubmittingMove(true);
    setActiveSquare(null);
    setLegalMoves([]);

    (async () => {
      try {
        const idToken = await firebaseUser.getIdToken();
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 8000);

        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tournamentMove`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ gameId, moveNotation: notation }),
          signal: controller.signal,
        });

        clearTimeout(timeout);

        if (!response.ok) {
          setPendingMove(null);
          await fetchMoves();
        }
      } catch (error) {
        setPendingMove(null);
        await fetchMoves();
      } finally {
        setIsSubmittingMove(false);
      }
    })();

    return true;
  };

  const squareStyles: { [square: string]: React.CSSProperties } = {};
  if (activeSquare) {
    squareStyles[activeSquare] = { background: 'rgba(172, 255, 195, 0.75)' };
  }

  const history = game.history({ verbose: true });
  const last = history[history.length - 1] as Move | undefined;
  if (last) {
    squareStyles[last.from] = { background: 'rgba(172, 255, 195, 0.75)' };
    squareStyles[last.to] = { background: 'rgba(172, 255, 195, 0.75)' };
  }

  legalMoves.forEach((square) => {
    const targetPiece = game.get(square as Square);
    squareStyles[square] = {
      background: targetPiece
        ? 'radial-gradient(circle, transparent 30%, rgba(0, 0, 0, 0.3) 31%, rgba(0, 0, 0, 0.3) 59%, transparent 50%)'
        : 'radial-gradient(circle, rgba(0, 0, 0, 0.3) 18%, transparent 20%)',
    };
  });

  if (game.inCheck()) {
    const board = game.board();
    for (let rank = 0; rank < 8; rank++) {
      for (let file = 0; file < 8; file++) {
        const p = board[rank][file];
        if (p && p.type === 'k' && p.color === game.turn()) {
          const square = String.fromCharCode('a'.charCodeAt(0) + file) + (8 - rank);
          squareStyles[square] = {
            ...squareStyles[square],
            background: 'radial-gradient(circle, rgba(219, 68, 52, 0.6) 70%, transparent 72%)',
            borderRadius: '4px',
          };
        }
      }
    }
  }

  // Custom chess pieces using Lichess's staunty set (3D style similar to chess.com Neo)
  const PIECE_SET = 'staunty'; // Options: staunty, cburnett, merida, california, etc.
  const pieceComponents: { [piece: string]: ({ squareWidth }: { squareWidth: number }) => React.ReactElement } = {};
  const pieceTypes = ['P', 'N', 'B', 'R', 'Q', 'K'];
  const colors = ['w', 'b'];

  colors.forEach(color => {
    pieceTypes.forEach(type => {
      const piece = `${color}${type}`;
      pieceComponents[piece] = ({ squareWidth }: { squareWidth: number }) => (
        <img
          src={`https://lichess1.org/assets/piece/${PIECE_SET}/${piece}.svg`}
          alt={piece}
          style={{ width: squareWidth, height: squareWidth }}
        />
      );
    });
  });

  // Wood texture styles for chess board
  const woodLightSquareStyle = {
    background: `
      linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.03) 50%, transparent 100%),
      linear-gradient(90deg,
        rgba(139,119,101,0.1) 0%,
        transparent 20%,
        rgba(139,119,101,0.08) 40%,
        transparent 60%,
        rgba(139,119,101,0.1) 80%,
        transparent 100%
      ),
      linear-gradient(180deg, rgba(255,255,255,0.1) 0%, transparent 50%, rgba(0,0,0,0.05) 100%),
      #e8d0aa
    `,
    backgroundSize: '100% 100%, 8px 100%, 100% 100%, 100% 100%',
  };

  const woodDarkSquareStyle = {
    background: `
      linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.05) 50%, transparent 100%),
      linear-gradient(90deg,
        rgba(0,0,0,0.15) 0%,
        transparent 15%,
        rgba(0,0,0,0.1) 35%,
        transparent 55%,
        rgba(0,0,0,0.12) 75%,
        transparent 100%
      ),
      linear-gradient(180deg, rgba(255,255,255,0.05) 0%, transparent 50%, rgba(0,0,0,0.08) 100%),
      #b58863
    `,
    backgroundSize: '100% 100%, 6px 100%, 100% 100%, 100% 100%',
  };

  return (
    <div className="chessContent">
      <div style={{ borderRadius: '4px', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)' }}>
        <Chessboard
          position={game.fen()}
          onPieceDrop={onDrop}
          onPieceDragBegin={onPieceDragBegin}
          onPieceDragEnd={() => setLegalMoves([])}
          snapToCursor
          arePiecesDraggable={Boolean(playerColor) && isHydrated && !isSubmittingMove && game.turn() === playerColor}
          customSquareStyles={squareStyles}
          customLightSquareStyle={woodLightSquareStyle}
          customDarkSquareStyle={woodDarkSquareStyle}
          customPieces={pieceComponents}
          boardOrientation={playerColor === 'b' ? 'black' : 'white'}
          boardWidth={670}
        />
      </div>
      <ChessGameInfo
        game={game}
        lastLocalMove={last ?? null}
        gameOver={false}
        tournamentID={tournamentId}
        gameId={gameId}
        playerColor={playerColor ?? 'nil'}
        whiteUsername={whiteUsername}
        blackUsername={blackUsername}
        startTime={startTime}
      />
    </div>
  );
}

export default ChessGame;
